// ============================================================================
// chatSearch.ts — ПОИСК ПО СООБЩЕНИЯМ ЧАТА (клиентская часть механизма).
//
// SSoT поиска — СЕРВЕР: MessengerService.SearchMessages (свои чаты, сниппет
// вокруг совпадения, новые первыми). Здесь только нормализация ответа,
// КЛИЕНТСКИЕ фильтры по уже найденным результатам и подсветка совпадений.
//
// ⚠️ ОГРАНИЧЕНИЯ СУЩЕСТВУЮЩЕГО API (proto/сервер НЕ меняем):
//   1) SearchMessages не принимает фильтры: дата/отправитель/вложения
//      фильтруются на клиенте по выданным результатам (сервер отдаёт до
//      `limit` совпадений — при большом числе совпадений фильтр видит не всё);
//   2) сервер отдаёт автора (senderName) и вложения (attachments — существующие
//      поля ChatMessage), поэтому «с вложениями/без» работает и в «всех чатах».
//      Для сообщений из загруженной ленты остаётся attachmentIndex — резерв,
//      если серверная выдача пришла без сведений о вложениях (hasAttachments
//      = null, такие результаты в режимах «с/без» не показываются и это
//      честно сказано в интерфейсе).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { stripAssistantSources } from '@/lib/assistantSources';

export const CHAT_SEARCH_MIN_QUERY = 2;
export const CHAT_SEARCH_LIMIT = 100;

export type ChatAttachmentsFilter = 'any' | 'with' | 'without';

export interface ChatSearchFilters {
  /** YYYY-MM-DD (локальная дата, включительно) */
  from: string;
  /** YYYY-MM-DD (локальная дата, включительно) */
  to: string;
  senderId: string;
  attachments: ChatAttachmentsFilter;
}

export const EMPTY_CHAT_SEARCH_FILTERS: ChatSearchFilters = {
  from: '',
  to: '',
  senderId: '',
  attachments: 'any',
};

export interface ChatSearchHit {
  id: string;
  conversationId: string;
  conversationTitle: string;
  snippet: string;
  text: string;
  createdAt: string;
  senderId: string;
  /** Имя автора с сервера (name → login); пусто — клиент резолвит по списку. */
  senderName: string;
  /** true/false — сведения сервера (или загруженной ленты); null — нет данных */
  hasAttachments: boolean | null;
}

/** Запрос готов к отправке (сервер требует ≥2 символов). */
export function isChatSearchQueryReady(query: string): boolean {
  return [...query.trim()].length >= CHAT_SEARCH_MIN_QUERY;
}

// Минимальные формы ответа RPC (proto/сервер не меняем; читаем только то, что
// реально используем; неизвестные поля — unknown, без any).
interface RawSearchMessage {
  id?: unknown;
  conversationId?: unknown;
  senderId?: unknown;
  senderName?: unknown;
  text?: unknown;
  createdAt?: unknown;
  attachments?: unknown;
}
interface RawSearchItem {
  message?: RawSearchMessage;
  conversationTitle?: unknown;
  snippet?: unknown;
}
interface SearchMessagesSdk {
  messenger: {
    searchMessages: (req: { query: string; conversationId: string; limit: number }) => Promise<{ items?: RawSearchItem[] }>;
  };
}

/** Поиск по сообщениям: conversationId пуст — по всем чатам пользователя. */
export async function searchChatMessages(
  query: string,
  conversationId = '',
  limit = CHAT_SEARCH_LIMIT,
): Promise<ChatSearchHit[]> {
  const sdk = getSDK() as unknown as SearchMessagesSdk;
  const res = await sdk.messenger.searchMessages({
    query: query.trim(),
    conversationId: conversationId || '',
    limit,
  });
  return (res.items || [])
    .map((it): ChatSearchHit => {
      const m = it.message || {};
      // protobuf-es инициализирует repeated-поля пустым массивом, JSON-транспорт
      // может не прислать поле вовсе — различаем «вложений нет» и «неизвестно».
      const attachments = Array.isArray(m.attachments) ? m.attachments : null;
      return {
        id: String(m.id || ''),
        conversationId: String(m.conversationId || ''),
        conversationTitle: String(it.conversationTitle || ''),
        snippet: stripAssistantSources(String(it.snippet || m.text || '')),
        text: stripAssistantSources(String(m.text || '')),
        createdAt: String(m.createdAt || ''),
        senderId: String(m.senderId || ''),
        senderName: String(m.senderName || ''),
        hasAttachments: attachments ? attachments.length > 0 : null,
      };
    })
    .filter((h) => !!h.id);
}

function parseDay(value: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? [Number(m[1]), Number(m[2]) - 1, Number(m[3])] : null;
}

function startOfDay(value: string): number | null {
  const p = parseDay(value);
  return p ? new Date(p[0], p[1], p[2], 0, 0, 0, 0).getTime() : null;
}

function endOfDay(value: string): number | null {
  const p = parseDay(value);
  return p ? new Date(p[0], p[1], p[2], 23, 59, 59, 999).getTime() : null;
}

/**
 * Клиентские фильтры по результатам поиска.
 * attachmentIndex — сведения о вложениях ИЗ ЗАГРУЖЕННОЙ ленты (id → есть ли
 * вложения). Для результатов вне индекса hasAttachments остаётся null.
 */
export function applyChatSearchFilters(
  hits: ChatSearchHit[],
  filters: ChatSearchFilters,
  attachmentIndex?: Map<string, boolean>,
): { items: ChatSearchHit[]; hiddenUnknownAttachments: number } {
  const from = startOfDay(filters.from);
  const to = endOfDay(filters.to);
  let hiddenUnknownAttachments = 0;

  const items = hits
    .map((h) => {
      // Сервер (SSoT) важнее локальной ленты; индекс ленты — резерв для
      // серверной выдачи без сведений о вложениях.
      const fallback = attachmentIndex?.get(h.id);
      const known = h.hasAttachments !== null ? h.hasAttachments : fallback;
      return known === undefined ? h : { ...h, hasAttachments: known };
    })
    .filter((h) => {
      if (filters.senderId && h.senderId !== filters.senderId) return false;
      if (from !== null || to !== null) {
        const ts = Date.parse(h.createdAt);
        // Дату не разобрали — при активном отборе по дате результат исключаем
        // (детерминированно: не показываем то, что не можем проверить).
        if (Number.isNaN(ts)) return false;
        if (from !== null && ts < from) return false;
        if (to !== null && ts > to) return false;
      }
      if (filters.attachments !== 'any') {
        if (h.hasAttachments === null) {
          hiddenUnknownAttachments += 1;
          return false;
        }
        if (filters.attachments === 'with' && !h.hasAttachments) return false;
        if (filters.attachments === 'without' && h.hasAttachments) return false;
      }
      return true;
    });

  return { items, hiddenUnknownAttachments };
}

export function chatSearchFilterCount(filters: ChatSearchFilters): number {
  let n = 0;
  if (filters.from) n += 1;
  if (filters.to) n += 1;
  if (filters.senderId) n += 1;
  if (filters.attachments !== 'any') n += 1;
  return n;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/** Разбиение текста на куски с флагами совпадения (регистронезависимо). */
export function highlightSegments(text: string, query: string): HighlightSegment[] {
  const q = query.trim();
  if (!q || !text) return [{ text, match: false }];
  const re = new RegExp(`(${escapeRegExp(q)})`, 'gi');
  return text.split(re).filter((part) => part !== '').map((part, i) => ({ text: part, match: i % 2 === 1 }));
}

/** Подпись времени результата: сегодня/вчера — время, иначе дата. */
export function chatSearchDateLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const yest = new Date();
  yest.setDate(yest.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return time;
  if (d.toDateString() === yest.toDateString()) return `вчера, ${time}`;
  const day = d.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
  return `${day}, ${time}`;
}
